import React, { useMemo, useState } from "react";
import "../styles/TagsFilter.css";

/**
 * Book-tags filter UI (matches Edit Book tags picker).
 * selected: array of tag_id values (string or number)
 * tagOptions: [{ tag_id, tag_name }]
 * onChange: (nextSelectedIds: Array) => void
 */
const TagsFilter = ({ tagOptions = [], selected = [], onChange }) => {
  const [query, setQuery] = useState("");

  const selectedSet = useMemo(
    () => new Set((selected || []).map((id) => String(id))),
    [selected]
  );

  const selectedTags = useMemo(
    () => tagOptions.filter((t) => selectedSet.has(String(t.tag_id))),
    [tagOptions, selectedSet]
  );

  const availableTags = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tagOptions
      .filter((t) => !selectedSet.has(String(t.tag_id)))
      .filter((t) => !q || String(t.tag_name || "").toLowerCase().includes(q))
      .sort((a, b) =>
        String(a.tag_name).localeCompare(String(b.tag_name), undefined, {
          sensitivity: "base",
        })
      );
  }, [tagOptions, selectedSet, query]);

  const addTag = (tag) => {
    if (tag?.tag_id == null || tag?.tag_id === "") return;
    const id = String(tag.tag_id);
    if (selectedSet.has(id)) return;
    onChange([...(selected || []).map(String), id]);
  };

  const removeTag = (tag) => {
    const id = String(tag.tag_id);
    onChange((selected || []).map(String).filter((v) => v !== id));
  };

  const tryAddFromInput = () => {
    const q = query.trim().toLowerCase();
    if (!q) return;
    const exact = tagOptions.find(
      (t) => String(t.tag_name || "").toLowerCase() === q
    );
    if (exact) {
      addTag(exact);
      setQuery("");
      return;
    }
    const first = availableTags[0];
    if (first) {
      addTag(first);
      setQuery("");
    }
  };

  return (
    <div className="tags-filter">
      <div className="tags-filter-input-wrap">
        <input
          type="text"
          className="tags-filter-input"
          placeholder="Search tags or type a new one"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              tryAddFromInput();
            }
          }}
        />
        <button
          type="button"
          className="tags-filter-add-btn"
          onClick={tryAddFromInput}
        >
          + Add
        </button>
      </div>

      {selectedTags.length > 0 ? (
        <div className="tags-filter-selected">
          {selectedTags.map((tag) => (
            <div key={tag.tag_id} className="tags-filter-selected-pill">
              <span>{tag.tag_name}</span>
              <button
                type="button"
                className="tags-filter-remove"
                onClick={() => removeTag(tag)}
                aria-label={`Remove ${tag.tag_name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}

      <p className="tags-filter-heading">
        {query.trim() ? "Matching tags" : "All tags"}
      </p>
      {availableTags.length > 0 ? (
        <div className="tags-filter-list">
          {availableTags.map((t) => (
            <button
              key={t.tag_id}
              type="button"
              className="tags-filter-item"
              onClick={() => addTag(t)}
            >
              {t.tag_name}
            </button>
          ))}
        </div>
      ) : (
        <p className="tags-filter-empty">
          {tagOptions.length === 0
            ? "No tags yet."
            : query.trim()
              ? "No matching tags."
              : "All tags are already selected."}
        </p>
      )}
    </div>
  );
};

export default TagsFilter;

/** Collect unique tags from book/group list + API list. */
export function mergeTagOptions(...lists) {
  const map = new Map();
  lists.flat().forEach((t) => {
    if (!t) return;
    const id = t.tag_id != null && t.tag_id !== "" ? String(t.tag_id) : null;
    const name = String(t.tag_name || "").trim();
    if (!name && !id) return;
    const key = id ? `id:${id}` : `name:${name.toLowerCase()}`;
    if (!map.has(key)) {
      map.set(key, {
        tag_id: id || name,
        tag_name: name || String(id),
      });
    }
  });
  return [...map.values()].sort((a, b) =>
    a.tag_name.localeCompare(b.tag_name, undefined, { sensitivity: "base" })
  );
}

export function tagsFromBooks(books = []) {
  const collected = [];
  books.forEach((book) => {
    (book.tags || []).forEach((t) => collected.push(t));
    (book.digital?.tags || []).forEach((t) => collected.push(t));
    (book.print?.tags || []).forEach((t) => collected.push(t));
  });
  return mergeTagOptions(collected);
}

export function bookMatchesSelectedTags(bookOrGroup, selectedTagIds) {
  if (!selectedTagIds?.length) return true;
  const tags = [
    ...(bookOrGroup?.tags || []),
    ...(bookOrGroup?.digital?.tags || []),
    ...(bookOrGroup?.print?.tags || []),
  ];
  if (!tags.length) return false;
  const selected = new Set(selectedTagIds.map(String));
  return tags.some(
    (t) =>
      selected.has(String(t.tag_id)) ||
      selected.has(String(t.tag_name || "").toLowerCase())
  );
}
